/**
 * AGREED PRICE on a LIVE engagement (the $40 / Tarveen fix) — PLACEMENT FIX 2026-06-15.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT + WHY
 * ─────────────────────────────────────────────────────────────────────────
 *  The capability the owner kept hitting (the Tarveen problem): the negotiated
 *  fee was only settable on a brand-new lead, so a USD engagement's advance gate
 *  fell back to the hard-coded ₹11,000 template figure and the money screen
 *  rendered it with a "$" → the bogus "$11,000".
 *
 *  This control sets the agreed currency + amount on the LIVE, already-converted
 *  project — any time, even after work has started — and SEEDS the gate amount
 *  from it (`seedGateAmounts`, written by `setProjectAgreedPrice`), so the gate
 *  EVERYWHERE it is displayed expects the agreed figure ($40) in the agreed
 *  currency. Every change is logged (`feeHistory`).
 *
 *  PLACEMENT (owner ruling 2026-06-15): this lives on the PAYMENT tab
 *  (`/money` → the per-engagement row), NOT on the Project page. It was
 *  extracted out of ProjectDetail into this shared component so the Payments
 *  surface owns the fee, and ProjectDetail no longer carries a money-edit
 *  control. (Setting price at quote-time on the Lead is a separate later piece.)
 *
 *  SINGLE SOURCE OF TRUTH for the DISPLAYED gate: `setProjectAgreedPrice`
 *  writes `projects/{pid}.gateAmounts` (+ currency + valueINR + feeHistory).
 *  Every gate readout (`moneyViewOf` on the project page, `projectMoney` on
 *  Payments, the record-payment flow) prefers that seeded per-gate amount over
 *  the template default — so the model AND the rendered screen agree.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { Box, Btn3, Card } from './ui';
import { money } from '../lib/moneyModel';
import type { Currency } from '../lib/firms';
import type { LiveProject } from '../lib/projectStore';
import { setProjectAgreedPrice } from '../lib/recordPaymentStore';

const editInput: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

export default function AgreedPricePanel({
  live,
  paid,
  actorName,
  onMsg,
}: {
  live: LiveProject;
  /** Amount already received (project currency units) — drives the balance. */
  paid: number;
  actorName: string;
  onMsg: (msg: string) => void;
}) {
  // The CURRENT agreed price: the per-gate seeded amounts sum to the agreed
  // value when present; otherwise the project is on the standing template rate
  // (no agreed override yet → "price not set" rather than a wrong "$11,000").
  const seededTotal = Object.values(live.gateAmounts ?? {}).reduce(
    (s, v) => s + (Number.isFinite(v) ? v : 0),
    0,
  );
  const hasAgreed =
    Object.keys(live.gateAmounts ?? {}).length > 0 || (live.valueINR ?? 0) > 0;
  const currentValue = seededTotal > 0 ? seededTotal : live.valueINR || 0;
  const currentCurrency: Currency = live.currency === 'USD' ? 'USD' : 'INR';

  const [editing, setEditing] = useState(false);
  const [curr, setCurr] = useState<Currency>(currentCurrency);
  const [valStr, setValStr] = useState(currentValue ? String(currentValue) : '');
  const [saving, setSaving] = useState(false);

  const newVal = Math.round(Number(valStr.replace(/[^0-9.]/g, '')) || 0);
  const balanceAfter = newVal - paid;

  // History newest-first for display (stored append order is newest-last).
  const history = [...(live.feeHistory ?? [])].reverse();

  function openEdit() {
    setCurr(currentCurrency);
    setValStr(currentValue ? String(currentValue) : '');
    setEditing(true);
  }

  async function save() {
    if (newVal <= 0) {
      onMsg('Enter an agreed amount greater than zero.');
      return;
    }
    setSaving(true);
    try {
      await setProjectAgreedPrice(
        live.pid,
        {
          tier: live.tierCode,
          currency: curr,
          value: newVal,
          prior: {
            currency: hasAgreed ? currentCurrency : null,
            value: hasAgreed ? currentValue : null,
          },
        },
        actorName,
      );
      onMsg(
        `Agreed price set to ${money(newVal, curr)} — the payment gate now expects this.`,
      );
      setEditing(false);
    } catch (e) {
      onMsg(e instanceof Error ? e.message : 'Could not save the agreed price.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      title="Agreed price"
      note="What this client pays for this project · editable any time · every change logged"
    >
      {!editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{ fontSize: 12, color: 'var(--ink-mute)', marginBottom: 4 }}
              >
                Current agreed price
              </div>
              {hasAgreed && currentValue > 0 ? (
                <div
                  data-testid="agreed-current"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 34,
                    fontWeight: 700,
                    lineHeight: 1,
                    color: 'var(--ink)',
                  }}
                >
                  {money(currentValue, currentCurrency)}
                </div>
              ) : (
                <div
                  data-testid="agreed-current"
                  style={{ fontSize: 15, fontWeight: 600, color: 'var(--warn)' }}
                >
                  Price not set
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 6 }}>
                {hasAgreed && currentValue > 0
                  ? 'The payment gate expects this figure in this currency.'
                  : 'Set the agreed price so the payment gate expects the real figure (not the standard template rate).'}
              </div>
            </div>
            <Btn3 intent="ink" size="sm" onClick={openEdit}>
              {hasAgreed && currentValue > 0 ? 'Edit agreed price' : 'Set agreed price'}
            </Btn3>
          </div>

          {history.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--ink-mute)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  margin: '4px 0 8px',
                }}
              >
                Change history
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                data-testid="fee-history"
              >
                {history.map((h, i) => (
                  <Card key={`${h.at}-${i}`} flat className="row-card">
                    <div style={{ fontSize: 13, color: 'var(--ink)' }} data-testid="fee-history-row">
                      {h.fromCurrency && h.fromValue != null ? (
                        <>
                          <s style={{ color: 'var(--ink-mute)' }}>
                            was {money(h.fromValue, h.fromCurrency)}
                          </s>{' '}
                          → now <b>{money(h.toValue, h.toCurrency)}</b>
                        </>
                      ) : (
                        <>
                          Created at <b>{money(h.toValue, h.toCurrency)}</b>
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 2 }}>
                      {h.note ? `${h.note} · ` : ''}by {h.by}
                      {h.at ? ` · ${new Date(h.at).toLocaleString()}` : ''}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
            Type the new figure. You&rsquo;ll see how the balance changes before
            you save. Work in progress is not affected.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap' }}>
            <select
              aria-label="Agreed price currency"
              value={curr}
              onChange={(e) => setCurr(e.target.value as Currency)}
              style={{ ...editInput, width: 120 }}
            >
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
            </select>
            <input
              aria-label="Agreed price amount"
              value={valStr}
              onChange={(e) => setValStr(e.target.value)}
              inputMode="decimal"
              placeholder="0"
              autoFocus
              style={{ ...editInput, width: 180, fontSize: 18, fontWeight: 700 }}
            />
          </div>

          <Card flat>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
              <span style={{ color: 'var(--ink-soft)' }}>New agreed fee</span>
              <b>{money(newVal, curr)}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
              <span style={{ color: 'var(--ink-soft)' }}>Already received</span>
              <b>{money(paid, curr)}</b>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 14,
                paddingTop: 8,
                marginTop: 4,
                borderTop: '1px solid var(--rule)',
              }}
            >
              <span style={{ fontWeight: 700, color: 'var(--ink)' }}>
                {balanceAfter < 0 ? 'To refund' : 'Balance becomes'}
              </span>
              <b style={{ color: balanceAfter <= 0 ? 'var(--good)' : 'var(--warn)' }}>
                {money(Math.abs(balanceAfter), curr)}
                {balanceAfter > 0 ? ' due' : ''}
              </b>
            </div>
          </Card>

          <div style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
            This change will be logged: {hasAgreed && currentValue > 0
              ? `${money(currentValue, currentCurrency)} → ${money(newVal, curr)}`
              : `set to ${money(newVal, curr)}`}{' '}
            · by {actorName} · now
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn3 intent="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Btn3>
            <Btn3 intent="ink" size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save new fee'}
            </Btn3>
          </div>
        </div>
      )}
    </Box>
  );
}
